import { ArrowLeft, CheckCircle2, Copy, Info, Receipt } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from '@/api/axios';
import { getInvoicePayments } from '@/api/invoices.api';
import {
  InvoiceStatus,
  InvoiceStreamEventType,
  type Invoice,
  type InvoicePaymentHistoryItem,
  type InvoicePaymentHistoryResponse,
  type PaymentCreateResponse,
} from '@/api/types';
import {
  getNetworkConfirmations,
  getNetworkLabel,
  getTokenLabel,
} from '@/common/consts';
import { PaymentConfirmationStatus } from '@/components/PaymentConfirmationStatus';
import { useInvoice } from '@/hooks';

type PaymentDetailsLocationState = PaymentCreateResponse & {
  invoiceId?: string;
};

function formatDuration(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return `${hh}:${mm}:${ss}`;
}

function getInvoiceRoute(invoiceId: string) {
  return `/invoice/${invoiceId}`;
}

function shouldReloadPaymentHistory(type: InvoiceStreamEventType) {
  return (
    type === InvoiceStreamEventType.PAYMENT_PARTIAL ||
    type === InvoiceStreamEventType.PAID
  );
}

function formatIntegerPart(integerPart: string) {
  return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatCryptoAmount(value: string | number) {
  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return '';
  }

  const sign = normalizedValue.startsWith('-') ? '-' : '';
  const unsignedValue = sign ? normalizedValue.slice(1) : normalizedValue;
  const [rawIntegerPart = '0', rawFractionPart = ''] = unsignedValue.split('.');
  const integerPart = rawIntegerPart.replace(/^0+(?=\d)/, '') || '0';
  const trimmedFractionPart = rawFractionPart.replace(/0+$/, '');
  const formattedIntegerPart = formatIntegerPart(integerPart);

  if (!rawFractionPart || !trimmedFractionPart) {
    return `${sign}${formattedIntegerPart}.00`;
  }

  return `${sign}${formattedIntegerPart}.${trimmedFractionPart}`;
}

function getCurrencyFractionDigits(currency: string) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
  } catch {
    return null;
  }
}

function formatAmountByCurrency(value: string | number, currency?: string) {
  if (!currency) {
    return formatCryptoAmount(value);
  }

  const fractionDigits = getCurrencyFractionDigits(currency);

  if (fractionDigits === null) {
    return formatCryptoAmount(value);
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return formatCryptoAmount(value);
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numericValue);
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatTransactionHash(txId: string) {
  const normalizedTxId = txId.trim();
  const prefixLength = normalizedTxId.startsWith('0x') ? 8 : 6;
  const suffixLength = 4;

  if (normalizedTxId.length <= prefixLength + suffixLength + 3) {
    return normalizedTxId;
  }

  return `${normalizedTxId.slice(0, prefixLength)}...${normalizedTxId.slice(-suffixLength)}`;
}

function PaymentSummary({
  invoice,
  formattedTotalAmount,
  formattedPaidAmount,
  formattedRemainingToPay,
}: {
  invoice: Invoice;
  formattedTotalAmount: string;
  formattedPaidAmount: string;
  formattedRemainingToPay: string;
}) {
  return (
    <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 shadow-sm">
      <h2 className="mb-6 text-lg font-semibold">Payment Summary</h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div>
          <p className="mb-2 text-xs text-gray-400">Total Amount</p>
          <p className="text-2xl font-bold">
            {formattedTotalAmount} {invoice.currency}
          </p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Paid Amount</p>
          <p className="text-2xl font-bold">
            {formattedPaidAmount} {invoice.currency}
          </p>
        </div>

        <div>
          <p className="mb-2 text-xs text-gray-400">Remaining to Pay</p>
          <p className="text-2xl font-bold">
            {formattedRemainingToPay} {invoice.currency}
          </p>
        </div>
      </div>
    </section>
  );
}

function TransactionHistory({
  transactions,
  loading,
  error,
  page,
  total,
  totalPages,
  onPrevPage,
  onNextPage,
}: {
  transactions: InvoicePaymentHistoryItem[];
  loading: boolean;
  error: string | null;
  page: number;
  total: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
}) {
  const [copiedTxId, setCopiedTxId] = useState<string | null>(null);
  const [failedTxId, setFailedTxId] = useState<string | null>(null);

  useEffect(() => {
    if (!copiedTxId && !failedTxId) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setCopiedTxId(null);
      setFailedTxId(null);
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [copiedTxId, failedTxId]);

  const handleCopyTxId = async (txId: string) => {
    if (!navigator.clipboard?.writeText) {
      setCopiedTxId(null);
      setFailedTxId(txId);
      return;
    }

    try {
      await navigator.clipboard.writeText(txId);
      setCopiedTxId(txId);
      setFailedTxId(null);
    } catch {
      setCopiedTxId(null);
      setFailedTxId(txId);
    }
  };

  return (
    <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Transaction History</h2>
        <p className="text-sm text-gray-400">
          {total > 0 ? `${total} recorded payment${total === 1 ? '' : 's'}` : ''}
        </p>
      </div>

      <div className="mb-4 hidden grid-cols-4 gap-4 border-b border-[#2a2b32] pb-3 text-xs text-gray-400 md:grid">
        <div>Date</div>
        <div>Asset</div>
        <div>Amount</div>
        <div>Tx ID</div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-[#2a2b32] bg-[#111215] px-4 py-6 text-sm text-gray-300">
          Loading payment history...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-700 bg-red-900/30 px-4 py-6 text-sm text-red-200">
          {error}
        </div>
      ) : transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <Receipt className="mb-3 h-8 w-8 opacity-50" />
          <p className="text-sm">There are no incoming transactions for this invoice.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div
                key={`${transaction.txId}-${transaction.date}`}
                className="grid grid-cols-1 gap-3 rounded-xl border border-[#2a2b32] bg-[#111215] px-4 py-3 text-sm text-white md:grid-cols-4 md:gap-4"
              >
                <div>
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Date</p>
                  <div>{formatDateTime(transaction.date)}</div>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Asset</p>
                  <div>{getTokenLabel(transaction.asset)}</div>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Amount</p>
                  <div>
                    {formatCryptoAmount(transaction.amount)} {transaction.asset}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs text-gray-500 md:hidden">Tx ID</p>
                  <div className="flex items-center gap-2">
                    <code className="truncate text-gray-300" title={transaction.txId}>
                      {formatTransactionHash(transaction.txId)}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        void handleCopyTxId(transaction.txId);
                      }}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[#3a3b45] bg-[#22242a] px-2 py-1 text-xs font-medium text-gray-100 transition-colors hover:bg-[#2a2d34]"
                      aria-label="Copy transaction hash"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                  {copiedTxId === transaction.txId && (
                    <p className="mt-1 text-xs text-emerald-300">Transaction hash copied.</p>
                  )}
                  {failedTxId === transaction.txId && (
                    <p className="mt-1 text-xs text-red-300">
                      Could not copy transaction hash.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#2a2b32] pt-4 text-sm">
              <p className="text-gray-400">
                Page {page} of {totalPages}
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onPrevPage}
                  disabled={page <= 1}
                  className="rounded-lg border border-[#2a2b32] px-3 py-2 text-white transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={onNextPage}
                  disabled={page >= totalPages}
                  className="rounded-lg border border-[#2a2b32] px-3 py-2 text-white transition-colors hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default function PaymentDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { invoiceId: routeInvoiceId } = useParams();
  const payment = location.state as PaymentDetailsLocationState | undefined;
  const invoiceId = routeInvoiceId ?? payment?.invoiceId;
  const [now, setNow] = useState(Date.now());
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsReloadKey, setPaymentsReloadKey] = useState(0);
  const [paymentsData, setPaymentsData] =
    useState<InvoicePaymentHistoryResponse | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const { invoice, loading: invoiceLoading, error: invoiceError } = useInvoice(
    invoiceId,
    {
      enableStream: true,
      onStreamEvent: (type) => {
        if (!shouldReloadPaymentHistory(type)) {
          return;
        }

        setPaymentsReloadKey((currentKey) => currentKey + 1);
      },
    },
  );
  const isInvoiceClosed =
    invoice?.status === InvoiceStatus.PAID ||
    invoice?.status === InvoiceStatus.CANCELLED;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (copyState === 'idle') {
      return undefined;
    }

    const timeout = window.setTimeout(() => setCopyState('idle'), 2000);

    return () => window.clearTimeout(timeout);
  }, [copyState]);

  useEffect(() => {
    if (!invoiceId || !isInvoiceClosed) {
      return;
    }

    navigate(getInvoiceRoute(invoiceId), { replace: true });
  }, [invoiceId, isInvoiceClosed, navigate]);

  useEffect(() => {
    setPaymentsPage(1);
  }, [invoiceId]);

  useEffect(() => {
    let isCancelled = false;

    if (!invoiceId) {
      setPaymentsData(null);
      setPaymentsError('Invoice ID missing');
      setPaymentsLoading(false);
      return;
    }

    setPaymentsLoading(true);
    setPaymentsError(null);

    getInvoicePayments(invoiceId, paymentsPage)
      .then((res) => {
        if (!isCancelled) {
          setPaymentsData(res.data);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setPaymentsData(null);
          setPaymentsError(
            getApiErrorMessage(error, 'Failed to load payment history'),
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setPaymentsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [invoiceId, paymentsPage, paymentsReloadKey]);

  const expiresAt = payment ? new Date(payment.expiredAt).getTime() : NaN;
  const hasValidExpiry = Number.isFinite(expiresAt);
  const timeLeftMs = hasValidExpiry ? Math.max(0, expiresAt - now) : 0;
  const isExpired = !hasValidExpiry || timeLeftMs <= 0;
  const networkLabel = payment
    ? getNetworkLabel(payment.network)
    : 'the selected network';
  const assetLabel = payment ? getTokenLabel(payment.asset) : 'the selected asset';
  const requiredConfirmations = payment
    ? (invoice?.paymentConfirmation?.requiredConfirmations ??
      getNetworkConfirmations(payment.network))
    : undefined;
  const confirmationNotice = requiredConfirmations
    ? `Your payment will be processed after ${requiredConfirmations} confirmed blocks on ${networkLabel}.`
    : `Your payment will be processed once enough confirmations are received on ${networkLabel}.`;
  const formattedRemainingToPay = invoice
    ? formatAmountByCurrency(invoice.remainingAmountWithFee, invoice.currency)
    : '';
  const formattedTotalAmount = invoice
    ? formatAmountByCurrency(invoice.totalAmount, invoice.currency)
    : '';
  const formattedPaidAmount = invoice
    ? formatAmountByCurrency(invoice.paidAmount, invoice.currency)
    : '';

  const qrUrl = useMemo(() => {
    if (!payment?.safeAddress) {
      return '';
    }

    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(payment.safeAddress)}`;
  }, [payment?.safeAddress]);

  const handleCopyAddress = async () => {
    if (!payment?.safeAddress || !navigator.clipboard?.writeText) {
      setCopyState('failed');
      return;
    }

    try {
      await navigator.clipboard.writeText(payment.safeAddress);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const handleBack = () => {
    if (invoiceId) {
      navigate(getInvoiceRoute(invoiceId));
      return;
    }

    navigate(-1);
  };

  if (invoiceId && isInvoiceClosed) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0d0e12] px-4 text-white md:p-8">
        <div className="pointer-events-none absolute right-[-5%] top-[-10%] h-[600px] w-[600px] rounded-full bg-orange-900/10 blur-[120px]" />
        <div className="pointer-events-none absolute right-[15%] top-[10%] h-2 w-2 rounded-full bg-yellow-100 opacity-50 blur-[2px]" />
        <div className="relative z-10 w-full max-w-xl rounded-2xl border border-[#2a2b32] bg-[#18191d] p-8 text-center">
          <h1 className="mb-3 text-2xl font-bold text-white">
            Returning to invoice
          </h1>
          <p className="text-gray-400">This invoice is no longer payable.</p>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0d0e12] px-4 text-white md:p-8">
        <div className="pointer-events-none absolute right-[-5%] top-[-10%] h-[600px] w-[600px] rounded-full bg-orange-900/10 blur-[120px]" />
        <div className="pointer-events-none absolute right-[15%] top-[10%] h-2 w-2 rounded-full bg-yellow-100 opacity-50 blur-[2px]" />
        <div className="relative z-10 w-full max-w-xl rounded-2xl border border-[#2a2b32] bg-[#18191d] p-8 text-center">
          <h1 className="mb-3 text-2xl font-bold text-white">
            No payment data
          </h1>
          <p className="mb-6 text-gray-400">
            Open this page after creating a payment from the invoice screen.
          </p>
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center justify-center rounded-lg bg-[#e97724] px-4 py-2 font-semibold text-white transition-colors hover:bg-[#d4691d]"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen justify-center overflow-hidden bg-[#0d0e12] p-4 text-white md:p-8 lg:p-12">
      <div className="pointer-events-none absolute right-[-5%] top-[-10%] h-[600px] w-[600px] rounded-full bg-orange-900/10 blur-[120px]" />
      <div className="pointer-events-none absolute right-[15%] top-[10%] h-2 w-2 rounded-full bg-yellow-100 opacity-50 blur-[2px]" />
      <div className="pointer-events-none absolute right-[25%] top-[5%] h-1.5 w-1.5 rounded-full bg-yellow-100 opacity-40 blur-[1px]" />

      <div className="relative z-10 flex w-full max-w-5xl flex-col gap-6">
        <header className="mb-2">
          <h1 className="mb-1 text-3xl font-bold">Payment Details</h1>
          <p className="text-sm text-gray-400">
            Invoice ID: {invoiceId ?? 'Not available'}
          </p>
        </header>

        <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 md:p-8">
          <div className="mb-6 flex items-start gap-3">
            <div className="mt-0.5 rounded-full bg-emerald-500/15 p-2 text-emerald-300">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Deposit instructions</h2>
              <p className="mt-1 text-sm text-gray-400">
                Send the exact amount to this deposit address before the timer
                expires.
              </p>
            </div>
          </div>

          <div className="mb-6 rounded-xl border border-sky-700/40 bg-sky-900/20 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-sky-500/20 p-1.5 text-sky-200">
                <Info className="h-4 w-4" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-sky-300">
                  Processing notice
                </p>
                <p className="mt-1 text-sm text-sky-100">{confirmationNotice}</p>
              </div>
            </div>
          </div>

          <PaymentConfirmationStatus
            confirmation={invoice?.paymentConfirmation}
            fallbackNetwork={payment.network}
            fallbackAsset={payment.asset}
            className="mb-6"
          />

          <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
            <div className="flex items-center justify-center rounded-xl border border-[#2a2b32] bg-[#111215] p-4">
              {qrUrl ? (
                <img
                  src={qrUrl}
                  alt="Deposit address QR code"
                  className="h-56 w-56 rounded-lg border border-[#2a2b32] bg-white p-2"
                />
              ) : (
                <div className="text-center text-sm text-gray-400">
                  QR code is unavailable.
                </div>
              )}
            </div>

            <div className="space-y-5 rounded-xl border border-[#2a2b32] bg-[#111215] p-5">
              <div>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  Amount to send
                </p>
                <p className="text-3xl font-semibold text-[#f49f60]">
                  {payment.amountExpected} {payment.asset}
                </p>
              </div>

              <div className="grid gap-3 text-sm text-gray-300">
                <p>
                  <span className="text-gray-500">Asset:</span> {assetLabel}
                </p>
                <p>
                  <span className="text-gray-500">Network:</span> {networkLabel}
                </p>
                <p>
                  <span className="text-gray-500">Active until:</span>{' '}
                  {formatDateTime(payment.expiredAt)}
                </p>
              </div>

              <div className="border-t border-[#2a2b32] pt-3">
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  Deposit address
                </label>
                <div className="flex items-start gap-2 rounded-lg border border-[#2a2b32] bg-[#18191d] p-3">
                  <code className="flex-1 break-all text-xs text-[#f8b57c]">
                    {payment.safeAddress}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyAddress}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[#3a3b45] bg-[#22242a] px-2.5 py-1.5 text-xs font-medium text-gray-100 transition-colors hover:bg-[#2a2d34]"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </button>
                </div>
                {copyState === 'copied' && (
                  <p className="mt-2 text-xs text-emerald-300">
                    Address copied to clipboard.
                  </p>
                )}
                {copyState === 'failed' && (
                  <p className="mt-2 text-xs text-red-300">
                    Could not copy address.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div
            className={`mt-5 rounded-lg border p-3 text-sm ${
              isExpired
                ? 'border-amber-700 bg-amber-900/20 text-amber-200'
                : 'border-[#e97724]/40 bg-[#e97724]/10 text-[#f8b57c]'
            }`}
          >
            {isExpired ? 'Address expired' : 'Time left'}: {formatDuration(timeLeftMs)}
          </div>

          <button
            type="button"
            onClick={handleBack}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#2a2b32] bg-[#22242a] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#2a2d34]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to invoice
          </button>
        </section>

        {invoice && (
          <PaymentSummary
            invoice={invoice}
            formattedTotalAmount={formattedTotalAmount}
            formattedPaidAmount={formattedPaidAmount}
            formattedRemainingToPay={formattedRemainingToPay}
          />
        )}

        {!invoice && invoiceLoading && (
          <section className="rounded-2xl border border-[#2a2b32] bg-[#18191d] p-6 text-sm text-gray-300">
            Loading payment summary...
          </section>
        )}

        {!invoice && invoiceError && (
          <section className="rounded-2xl border border-red-700 bg-red-900/30 p-6 text-sm text-red-200">
            {invoiceError}
          </section>
        )}

        <TransactionHistory
          transactions={paymentsData?.data ?? []}
          loading={paymentsLoading}
          error={paymentsError}
          page={paymentsData?.page ?? paymentsPage}
          total={paymentsData?.total ?? 0}
          totalPages={paymentsData?.totalPages ?? 0}
          onPrevPage={() => {
            setPaymentsPage((currentPage) => Math.max(currentPage - 1, 1));
          }}
          onNextPage={() => {
            setPaymentsPage((currentPage) => {
              const totalPages = paymentsData?.totalPages ?? currentPage;
              return Math.min(currentPage + 1, totalPages);
            });
          }}
        />
      </div>
    </div>
  );
}
